import { Injectable, Injector } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { from, NEVER, Observable, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { catchError, switchMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { DialogComponent } from '../modules/shared/components/dialog/dialog.component';
import { LocaleService } from '../services/locale.service';
import { PatientService } from '../services/patient.service';
import { LoadingSpinnerService } from '../services/loading-spinner.service';
import { HttpHeadersService } from '../services/http-headers.service';

interface SessionExpiredDialogData {
  title: string;
  message: string;
  canCancel: boolean;
  buttonText: string;
  action: () => void;
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private matDialog: MatDialog,
    private spinnerService: LoadingSpinnerService,
    private httpHeadersService: HttpHeadersService,
    private injector: Injector
  ) {}

  // Resolve services lazily to avoid the HttpClient -> interceptor -> HttpClient DI loop.
  private get authService(): AuthService {
    return this.injector.get(AuthService);
  }

  private get localeService(): LocaleService {
    return this.injector.get(LocaleService);
  }

  private get patientService(): PatientService {
    return this.injector.get(PatientService);
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.url.startsWith('https://maps.googleapis.com/') || request.url.startsWith('https://places.googleapis.com/')) {
      return next.handle(request);
    }

    if (this.authService.usesIdentityPlatform()) {
      return from(this.authService.getIdentityPlatformToken()).pipe(
        switchMap((token) => this.handleRequest(this.addHeaders(request, token), next))
      );
    }

    return this.handleRequest(this.addHeaders(request, this.authService.getToken()), next);
  }

  private handleRequest(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error) => {
        if (error?.status === 401 && this.authService.hasSessionContext()) {
          const inAppointment = this.authService.handleUnauthorized();
          return inAppointment ? throwError(error) : NEVER;
        }

        const extra = error?.error?.extra || {};

        if (error?.error?.type === 'providerNotActive') {
          const action = () => {
            const url = extra?.loginDisabledRedirectUrl;
            if (url) {
              window.location.href = url;
            }
          };

          this.showSessionExpiredDialog({
            title: 'restricted_access',
            message: extra?.loginDisabledMessage,
            buttonText: extra?.loginDisabledButtonText,
            canCancel: false,
            action
          });

          return NEVER;
        }

        return throwError(error);
      })
    );
  }

  private showSessionExpiredDialog({ title, message, canCancel, buttonText, action }: SessionExpiredDialogData) {
    this.matDialog.closeAll();
    this.spinnerService.hideSpinner();
    this.matDialog.open(DialogComponent, {
      width: '80%',
      maxWidth: '350px',
      disableClose: true,
      data: {
        title,
        message,
        canCancel,
        actions: [
          {
            message: buttonText,
            action: action
          }
        ]
      }
    });
  }

  private addHeaders(request: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
    const headers = this.httpHeadersService.getHeaders({
      token: token || undefined,
      locale: this.localeService.getHeaderLocale(),
      patient: this.patientService.patient,
      providerId: this.authService.getIdentityPlatformProviderId()
    });

    request = request.clone({
      setHeaders: {
        ...headers
      }
    });

    return request;
  }
}
